import React, { type FC } from 'react'

import styled from '@emotion/styled'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Collapsible,
  Flex,
  FormElement,
  Select,
  TEXT_VARIANT_LIGHT,
  Text,
  Toggle,
} from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import {
  modalSubscribeChangeCallback,
  modalSubscribeToggleFieldCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalSubscribe'
import {
  modalSubscribeHasMessageFeedbackState,
  modalSubscribeState,
} from 'Pages/RecipientsList/recoil/state/modalSubscribe'

import {
  fetchMessageById,
  fetchMessages,
} from 'Pages/RecipientsList/recoil/services/asyncSelectConfirmationMessages'

const StyledToggleLabel = styled.label`
  cursor: pointer;
`

const InputConfirmation: FC = () => {
  const intl = useIntl()

  const { sendConfirmation, idConfirmationMessage } =
    useRecoilValue(modalSubscribeState)

  const showFeedback = useRecoilValue(modalSubscribeHasMessageFeedbackState)

  const toggle = useRecoilCallback(modalSubscribeToggleFieldCallback, [])
  const change = useRecoilCallback(modalSubscribeChangeCallback, [])

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'modal-subscribe',
      value: idConfirmationMessage,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchMessages(''),
      fetchItemsByTerm: fetchMessages,
      fetchItemById: fetchMessageById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleToggle = (): void => {
    void toggle('sendConfirmation')
  }

  const handleChangeMessage = (v: number): void => {
    void change({
      idConfirmationMessage: v,
      idConfirmationMessageIsDirty: true,
    })
  }

  return (
    <section>
      <Flex flexDirection="row" alignItems="center" gap="8px">
        <Toggle
          data-mu-test="toggle-send-confirmation"
          variant="sm"
          checked={sendConfirmation}
          onChange={handleToggle}
          id="toggle-send-confirmation"
        />

        <StyledToggleLabel htmlFor="toggle-send-confirmation">
          {intl.formatMessage({
            id: 'Load.RecipientsList.ModalSubscribeConfirmationMessageLabel',
          })}
        </StyledToggleLabel>
      </Flex>

      <Flex flexDirection="column" margin="8px 0 0 45px">
        <Text variant={TEXT_VARIANT_LIGHT}>
          {intl.formatMessage({
            id: 'Load.RecipientsList.ModalSubscribeConfirmationMessageDescription',
          })}
        </Text>

        <Collapsible collapsed={!sendConfirmation}>
          <Flex flexDirection="column" padding="8px 0 0 0">
            <FormElement
              variant={showFeedback ? 'danger' : undefined}
              feedback={
                showFeedback
                  ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
                  : undefined
              }
            >
              <Select
                data-mu-test="input-confirmation"
                value={idConfirmationMessage}
                options={options}
                loading={loading}
                onChange={handleChangeMessage}
                onSearch={handleSearch}
                hint={hint}
                hintNoResults={hintNoResults}
                memoSelectOption
                error={showFeedback}
                placeholder={intl.formatMessage({
                  id: 'Load.RecipientsList.ModalPendingSelectMessage',
                })}
              />
            </FormElement>
          </Flex>
        </Collapsible>
      </Flex>
    </section>
  )
}

export default InputConfirmation
